import { useTranslation } from "@sagot/i18n";
import { CodeBlock, Icon, Segmented } from "@sagot/ui";
import { useState } from "react";

const K = "deployKnowledge.widgetStudio.install";
const SERVER_LANGUAGES = ["node", "php", "python"] as const;
type ServerLanguage = (typeof SERVER_LANGUAGES)[number];
const NOTES = ["secret", "ttl", "refresh", "logout"] as const;

interface IdentityGuideProps {
  /** `SnippetsOut.identity`: `client` plus one `/sagot-token` example per server language. */
  examples: Readonly<Record<string, string>>;
}

/**
 * Signed-in visitors: the page-side `identify` call (token fetched with `getToken`, `logout` on
 * sign-out) and a `/sagot-token` endpoint per server language. The secret stays on the server.
 */
export function IdentityGuide({ examples }: IdentityGuideProps) {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<ServerLanguage>("node");
  const copy = { copyLabel: t("common.copy"), copiedLabel: t("common.copied") };
  return (
    <div className="space-y-5" data-testid="identity-guide">
      <p className="text-xs text-fg-muted">{t(`${K}.identityIntro`)}</p>
      <section className="space-y-2" aria-labelledby="identity-client-title">
        <h3 id="identity-client-title" className="text-sm font-bold">{t(`${K}.identityClient`)}</h3>
        <CodeBlock code={examples.client ?? ""} label={t(`${K}.identityClient`)} {...copy} />
      </section>
      <section className="space-y-2" aria-labelledby="identity-server-title">
        <h3 id="identity-server-title" className="text-sm font-bold">{t(`${K}.identityServer`)}</h3>
        <p className="text-xs text-fg-muted">{t(`${K}.identityServerHint`)}</p>
        <Segmented
          aria-label={t(`${K}.identityLang`)}
          value={language}
          onValueChange={setLanguage}
          options={SERVER_LANGUAGES.map((value) => ({ value, label: t(`${K}.identityLangs.${value}`) }))}
        />
        <CodeBlock code={examples[language] ?? ""} label={t(`${K}.identityLangs.${language}`)} {...copy} />
      </section>
      <section className="rounded-lg border border-line bg-surface-2 p-3.5" aria-labelledby="identity-notes-title">
        <h3 id="identity-notes-title" className="flex items-center gap-1.5 text-sm font-bold">
          <Icon name="shield-check" className="size-4 text-primary-ink" />
          {t(`${K}.identityNotes.title`)}
        </h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-fg-muted">
          {NOTES.map((note) => <li key={note}>{t(`${K}.identityNotes.${note}`)}</li>)}
        </ul>
      </section>
    </div>
  );
}
